// ViewsPanel.jsx — Named crop views for map data-item documents.

import React, {useState} from 'react';
import {
    Box, Stack, Typography, IconButton, Tooltip, TextField, Button,
} from '@mui/material';
import {DEFAULT_VIEW_ID} from '../schema/mapSchema';
import AddRounded from '@mui/icons-material/AddRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import MyLocationRounded from '@mui/icons-material/MyLocationRounded';
import CropFreeRounded from '@mui/icons-material/CropFreeRounded';

// rerender-memo: memoized so it skips re-render when views/callbacks are unchanged — mirrors
// the pattern already used on ContextPanel/ShapeForm/LayerPanel.
const ViewsPanel = React.memo(function ViewsPanel({
    views,
    selectedViewId,
    onSelectView,
    onCreateView,
    onUpdateViewCamera,
    onRenameView,
    onDeleteView,
    onFlyToView,
}) {
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState('');
    const [newViewName, setNewViewName] = useState('');
    const [showNameInput, setShowNameInput] = useState(false);

    const handleCreateView = () => {
        const name = newViewName.trim() || `View ${views.length + 1}`;
        onCreateView?.(name);
        setNewViewName('');
        setShowNameInput(false);
    };

    return (
        <Stack>
            <Box>
                {views.length === 0 && (
                    <Typography variant="body2" sx={{px: 1.5, py: 1.5, color: 'text.disabled', fontSize: 12}}>
                        No views yet. Pan/zoom the map, then capture the current extent as a view.
                    </Typography>
                )}
                <Stack component="ul" sx={{m: 0, p: 0, listStyle: 'none'}}>
                    <Stack
                        component="li"
                        direction="row"
                        alignItems="center"
                        gap={0.5}
                        sx={(theme) => ({
                            px: 1, py: 0.75,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            bgcolor: selectedViewId === DEFAULT_VIEW_ID ? 'action.selected' : 'transparent',
                        })}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                flex: 1, fontSize: 12, cursor: 'pointer',
                                fontWeight: selectedViewId === DEFAULT_VIEW_ID ? 600 : 400,
                                color: selectedViewId === DEFAULT_VIEW_ID ? 'primary.main' : 'text.primary',
                            }}
                            onClick={() => onSelectView?.(DEFAULT_VIEW_ID)}
                        >
                            All markings
                        </Typography>
                        <Tooltip title="Fit all shapes">
                            <IconButton size="small" onClick={() => onFlyToView?.(DEFAULT_VIEW_ID)}>
                                <MyLocationRounded sx={{fontSize: 14}}/>
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    {views.map((view) => {
                        const isSelected = view.viewId === selectedViewId;
                        const isEditing = editingId === view.viewId;
                        return (
                            <Stack
                                key={view.viewId}
                                component="li"
                                direction="row"
                                alignItems="center"
                                gap={0.5}
                                sx={(theme) => ({
                                    px: 1, py: 0.75,
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    bgcolor: isSelected ? 'action.selected' : 'transparent',
                                })}
                            >
                                {isEditing ? (
                                    <TextField
                                        size="small"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                onRenameView?.(view.viewId, editName.trim() || 'View');
                                                setEditingId(null);
                                            }
                                        }}
                                        autoFocus
                                        sx={{flex: 1}}
                                    />
                                ) : (
                                    <Typography
                                        variant="body2"
                                        sx={{flex: 1, fontSize: 12, cursor: 'pointer',
                                            fontWeight: isSelected ? 600 : 400,
                                            color: isSelected ? 'primary.main' : 'text.primary'}}
                                        onClick={() => onSelectView?.(view.viewId)}
                                    >
                                        {view.name}
                                    </Typography>
                                )}
                                <Tooltip title="Jump to view">
                                    <IconButton size="small" onClick={() => onFlyToView?.(view.viewId)}>
                                        <MyLocationRounded sx={{fontSize: 14}}/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Update crop to current extent">
                                    <IconButton size="small" onClick={() => onUpdateViewCamera?.(view.viewId)}>
                                        <CropFreeRounded sx={{fontSize: 14}}/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Rename">
                                    <IconButton size="small" onClick={() => {
                                        setEditingId(view.viewId);
                                        setEditName(view.name || '');
                                    }}>
                                        <EditRounded sx={{fontSize: 14}}/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                    <IconButton size="small" color="error" onClick={() => onDeleteView?.(view.viewId)}>
                                        <DeleteRounded sx={{fontSize: 14}}/>
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        );
                    })}
                </Stack>
            </Box>
            <Box sx={{px: 1, py: 1, borderTop: 1, borderColor: 'divider'}}>
                {showNameInput ? (
                    <Stack direction="row" gap={0.5}>
                        <TextField
                            size="small"
                            placeholder="View name"
                            value={newViewName}
                            onChange={(e) => setNewViewName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleCreateView(); }}
                            autoFocus
                            sx={{flex: 1}}
                        />
                        <IconButton size="small" color="primary" onClick={handleCreateView}>
                            <CheckRounded sx={{fontSize: 16}}/>
                        </IconButton>
                    </Stack>
                ) : (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddRounded sx={{fontSize: 14}}/>}
                        onClick={() => setShowNameInput(true)}
                        sx={{width: '100%', fontSize: 11, textTransform: 'none'}}
                    >
                        Capture current extent
                    </Button>
                )}
            </Box>
        </Stack>
    );
});

export default ViewsPanel;
